import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CalendarView = ({ 
  selectedDate, 
  onDateSelect, 
  interviews, 
  onInterviewClick, 
  onTimeSlotSelect,
  viewMode,
  onViewModeChange 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(new Date());

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
  ];

  const interviewTypes = {
    'phone': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: 'Phone' },
    'video': { color: 'bg-green-100 text-green-800 border-green-200', icon: 'Video' },
    'in-person': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: 'MapPin' }
  };

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days?.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getWeekDays = (date) => {
    const startOfWeek = new Date(date);
    let day = startOfWeek?.getDay();
    const diff = startOfWeek?.getDate() - day;
    startOfWeek?.setDate(diff);

    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      let day = new Date(startOfWeek);
      day?.setDate(startOfWeek?.getDate() + i);
      weekDays?.push(day);
    }
    return weekDays;
  };

  const getInterviewsForDate = (date) => {
    if (!date) return [];
    const dateStr = date?.toDateString();
    return interviews?.filter(interview => 
      new Date(interview.dateTime)?.toDateString() === dateStr
    );
  };

  const getInterviewsForTimeSlot = (date, time) => {
    const dateStr = date?.toDateString();
    return interviews?.filter(interview => {
      const interviewDate = new Date(interview.dateTime);
      return interviewDate?.toDateString() === dateStr && 
             interviewDate?.toTimeString()?.substring(0, 5) === time;
    });
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const isSelected = (date) => {
    if (!date || !selectedDate) return false;
    return date?.toDateString() === selectedDate?.toDateString();
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth?.setMonth(currentMonth?.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const navigateWeek = (direction) => {
    const newWeek = new Date(currentWeek);
    newWeek?.setDate(currentWeek?.getDate() + (direction * 7));
    setCurrentWeek(newWeek);
  };

  const formatTime = (dateTime) => {
    return new Date(dateTime)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const renderMonthView = () => {
    const days = getDaysInMonth(currentMonth);
    const monthName = currentMonth?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return (
      <div className="bg-surface rounded-lg border border-border">
        {/* Month Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">{monthName}</h3>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              onClick={() => navigateMonth(-1)}
            />
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              onClick={() => navigateMonth(1)}
            />
          </div>
        </div>
        {/* Calendar Grid */}
        <div className="p-4">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {days?.map((day, index) => {
              const dayInterviews = getInterviewsForDate(day);
              
              return (
                <button
                  key={index}
                  onClick={() => day && onDateSelect(day)}
                  disabled={!day}
                  className={`
                    relative p-2 h-20 text-sm border border-border rounded-lg
                    transition-all duration-200 micro-feedback
                    ${!day ? 'invisible' : ''}
                    ${isToday(day) ? 'bg-primary/10 border-primary/30' : 'bg-surface hover:bg-muted'}
                    ${isSelected(day) ? 'bg-primary text-primary-foreground' : ''}
                    ${dayInterviews?.length > 0 ? 'border-accent/50' : ''}
                  `}
                >
                  {day && (
                    <>
                      <span className={`font-medium ${isSelected(day) ? 'text-primary-foreground' : 'text-foreground'}`}>
                        {day?.getDate()}
                      </span>
                      {dayInterviews?.length > 0 && (
                        <div className="absolute bottom-1 left-1 right-1">
                          <div className="flex flex-wrap gap-0.5">
                            {dayInterviews?.slice(0, 3)?.map((interview, idx) => (
                              <div
                                key={idx}
                                className={`w-1.5 h-1.5 rounded-full ${
                                  interviewTypes?.[interview?.type]?.color?.split(' ')?.[0] || 'bg-accent'
                                }`}
                              />
                            ))}
                            {dayInterviews?.length > 3 && (
                              <span className="text-xs text-muted-foreground">+{dayInterviews?.length - 3}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays(currentWeek);
    const weekRange = `${weekDays?.[0]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDays?.[6]?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

    return (
      <div className="bg-surface rounded-lg border border-border">
        {/* Week Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">{weekRange}</h3>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              onClick={() => navigateWeek(-1)}
            />
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              onClick={() => navigateWeek(1)}
            />
          </div>
        </div>
        {/* Week Grid */}
        <div className="p-4">
          {/* Day Headers */}
          <div className="grid grid-cols-8 gap-2 mb-4">
            <div className="p-2 text-sm font-medium text-muted-foreground">Time</div>
            {weekDays?.map(day => (
              <div
                key={day?.toDateString()}
                className={`p-2 text-center text-sm font-medium rounded-lg ${
                  isToday(day) ? 'bg-primary text-primary-foreground' : 'text-foreground'
                }`}
              >
                <div>{day?.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                <div className="text-lg font-semibold">{day?.getDate()}</div>
              </div>
            ))}
          </div>

          {/* Time Slots */}
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {timeSlots?.map(time => (
              <div key={time} className="grid grid-cols-8 gap-2">
                <div className="p-2 text-sm text-muted-foreground font-medium">
                  {time}
                </div>
                {weekDays?.map(day => {
                  const slotInterviews = getInterviewsForTimeSlot(day, time);
                  
                  return (
                    <button
                      key={`${day?.toDateString()}-${time}`}
                      onClick={() => onTimeSlotSelect(day, time)}
                      className={`
                        p-2 h-12 text-xs border border-border rounded-lg
                        transition-all duration-200 micro-feedback
                        ${slotInterviews?.length > 0 
                          ? 'bg-accent/10 border-accent/30 hover:bg-accent/20' :'bg-surface hover:bg-muted'
                        }
                      `}
                    >
                      {slotInterviews?.map((interview, idx) => (
                        <div
                          key={idx}
                          onClick={(e) => {
                            e?.stopPropagation();
                            onInterviewClick(interview);
                          }}
                          className={`
                            px-1 py-0.5 rounded text-xs truncate
                            ${interviewTypes?.[interview?.type]?.color || 'bg-accent/20 text-accent-foreground'}
                          `}
                        >
                          {interview?.candidateName}
                        </div>
                      ))}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={20} color="var(--color-primary)" />
          <h2 className="text-xl font-semibold text-foreground">Calendar</h2>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant={viewMode === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('month')}
          >
            Month
          </Button>
          <Button
            variant={viewMode === 'week' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('week')}
          >
            Week
          </Button>
        </div>
      </div>
      {/* Calendar Content */}
      {viewMode === 'month' ? renderMonthView() : renderWeekView()}
      {/* Legend */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-foreground mb-3">Interview Types</h4>
        <div className="flex flex-wrap gap-4">
          {Object.entries(interviewTypes)?.map(([type, config]) => (
            <div key={type} className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${config?.color?.split(' ')?.[0]}`} />
              <Icon name={config?.icon} size={14} />
              <span className="text-sm text-foreground capitalize">{type?.replace('-', ' ')}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;